import { useConfig } from "@jti/app-context/useConfig";
import { useCheckoutConfiguration } from "@jti/commerce/providers";
import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import { type Contact } from "@jti/fe-common";
import { useSelector } from "@jti/store/reduxHooks";
import { ContactFields, ContactInfo, Telephone, useTranslations } from "@jti/ui";

import { PrefilledPhoneContact } from "../PrefilledPhoneContact/PrefilledPhoneContact";

import styles from "../ShippingContact.module.scss";

const telephonePrefix = "editPhone";

type Props = {
  sameRecipient?: boolean;
  contact?: Contact;
  isPhoneValidationErrorVisible?: boolean;
  isAccountPhoneNumberInvalid?: boolean;
};

export const ShippingContactBottom = ({
  sameRecipient,
  contact,
  isPhoneValidationErrorVisible,
  isAccountPhoneNumberInvalid,
}: Props) => {
  const { CHECKOUT_PHONE_REQUIRED } = useConfig();
  const { __ } = useTranslations();
  const { contactConfig } = useCheckoutConfiguration();
  const populatedPhoneInput = contactConfig?.populatedPhoneInput;
  const storeConfig = useSelector((state) => state.cart.storeConfig);

  const isPhoneNumberRequired = /true/i.test(CHECKOUT_PHONE_REQUIRED) || isAccountPhoneNumberInvalid;
  const shouldHidePhone =
    isPhoneValidationErrorVisible ||
    (populatedPhoneInput && !!contact?.telephone) ||
    (!populatedPhoneInput && !contact?.telephone && !isPhoneNumberRequired);

  return sameRecipient && contact ? (
    <>
      <ContactInfo contact={contact} hidePhone={shouldHidePhone} />
      {!isPhoneValidationErrorVisible && populatedPhoneInput && (
        <PrefilledPhoneContact
          dataTestId="prefilled-phone-contact"
          telephone={contact.telephone}
          isPhoneNumberRequired={isPhoneNumberRequired}
        />
      )}

      {isPhoneValidationErrorVisible && (
        <>
          <div className={styles.messageBox}>
            <AlertBanner
              tone="warning"
              data-testid="message-box"
              description={__("cart.checkout.warning.missing_phone_number")}
            />
          </div>

          <Telephone
            fieldName={(name) => `${telephonePrefix}.${name}`}
            isRequired={isPhoneNumberRequired}
            isInfoMessageVisible
          />
        </>
      )}
    </>
  ) : (
    <ContactFields
      fieldName={(name) => `shippingContact.${name}`}
      isSingleNameField={storeConfig.allow_empty_firstname}
      isPhoneRequired={isPhoneNumberRequired}
    />
  );
};
